import { useCompanyData } from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { Loading } from '@commenda/kakashi';
import mixpanel, { MIXPANEL_SURFACE_AREA } from '@commenda/mixpanel';
import { CommendaLayout } from 'components/Layouts/Layout/CommendaLayout';
import { useConfig } from 'context/useConfig';
import { useOnboardingRedirect } from 'hooks/useOnboardingRedirect';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { ActiveServicesTable } from './ActiveServicesTable';
import { EntitiesSection } from './EntitiesSection';
import { GlobalInbox } from './GlobalInbox';
import { GreetingHeader } from './GreetingHeader';
import { HomeCalendarAgenda } from './HomeCalendarAgenda';
import { PendingServiceRequestModal } from './PendingServiceRequestModal';
import { PointOfContact } from './PointOfContact';

const RightSidebar = ({
  companyId,
  draftMessage,
}: {
  companyId: number | null;
  draftMessage?: string;
}) => {
  const showCalendarAgenda = isFeatureLaunched(
    FeatureLaunch.HOME_CALENDAR_AGENDA
  );

  return (
    <div className="flex flex-col gap-md h-full">
      <PointOfContact />
      {companyId && showCalendarAgenda ? (
        <HomeCalendarAgenda companyId={companyId} />
      ) : (
        companyId && (
          <GlobalInbox companyId={companyId} draftMessage={draftMessage} />
        )
      )}
    </div>
  );
};

export const HomeScreenDashboard = () => {
  const { isLoading } = useOnboardingRedirect();
  const { companyId, user, isLoading: isConfigLoading } = useConfig();
  const { data: company } = useCompanyData(companyId);
  const router = useRouter();

  const jurisdiction =
    typeof router.query.jurisdiction === 'string'
      ? router.query.jurisdiction
      : undefined;
  const draftMessage = jurisdiction
    ? `Hi, I'm looking for help managing my entity in ${jurisdiction}. Could you assist me with this?`
    : undefined;

  // Track Mixpanel event with user and company information when company data is loaded
  const hasTrackedEvent = useRef(false);
  useEffect(() => {
    if (
      !isConfigLoading &&
      company &&
      user &&
      companyId &&
      !hasTrackedEvent.current
    ) {
      mixpanel.track('ACTIVE_ENTERPRISE_USER', {
        surface_area: MIXPANEL_SURFACE_AREA.CLIENT_HOME,
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        company_id: company.id,
        company_name: company.companyDisplayName || null,
        company_display_name: company.companyDisplayName || null,
      });

      hasTrackedEvent.current = true;
    }
  }, [isConfigLoading, company, user, companyId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <Loading />
      </div>
    );
  }

  return (
    <CommendaLayout
      aside={{
        content: (
          <RightSidebar companyId={companyId} draftMessage={draftMessage} />
        ),
        variant: 'plain',
        width: '400px',
      }}
      disableContentPadding
    >
      <PendingServiceRequestModal />
      <div className="flex flex-col">
        <div className="px-3xl py-xl">
          <GreetingHeader />
        </div>
        {companyId && <EntitiesSection companyId={companyId} />}
        {companyId && <ActiveServicesTable companyId={companyId} />}
      </div>
    </CommendaLayout>
  );
};
